import { customElement } from '../../core/ids-decorators';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import { IdsDirection } from '../../core/ids-attributes';
import type IdsDataGrid from './ids-data-grid';
import type IdsDropdown from '../ids-dropdown/ids-dropdown';
import type IdsInput from '../ids-input/ids-input';
import type IdsLookup from '../ids-lookup/ids-lookup';
import type IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import type { IdsDataGridColumn } from './ids-data-grid-column';
import type IdsDataGridRow from './ids-data-grid-row';
import type { IdsDataGridEditor, IdsDataGridEditors } from './ids-data-grid-editors';
import type IdsDatePickerPopup from '../ids-date-picker/ids-date-picker-popup';
import { getSpecs } from '../../utils/ids-device-env-specs-utils/ids-device-env-specs-utils';
import IdsListBoxOption from '../ids-list-box/ids-list-box-option';

export type IdsDataGridActivationReason = 'keyboard' | 'method' | 'internal' | 'redraw' | 'mouse' | 'click';

@customElement('ids-data-grid-cell')
export default class IdsDataGridCell extends IdsEventsMixin(IdsElement) {
  rootNode?: any;

  isInValid = false;

  /** The editor element */
  editor?: IdsDataGridEditor;

  constructor() {
    super({ noShadowRoot: true });
  }

  connectedCallback(): void {
    // NOTE: bypassing super.connectedCallback() for performance reasons
    this.renderCell();
    this.tabIndex = this.rowIndex === 0 && this.columnIndex === 0 ? 0 : -1;
    this.setAttribute('tabindex', String(this.tabIndex));

    this.addEventListener('focus', this.onFocus);

    if (this.isDraggable) {
      this?.setAttribute('draggable', 'true');
    }
  }

  get parentRow(): IdsDataGridRow | null {
    return this.parentElement as IdsDataGridRow;
  }

  onFocus() {
    this.dataGrid.lastActiveCell = this.activate(false, 'keyboard');
    this.dataGrid.offEvent('blur.ids-data-grid-cell', this);
    this.dataGrid.onEvent('blur.ids-data-grid-cell', this, this.onBlur);
  }

  onBlur() {
    this.dataGrid.offEvent('focus.ids-data-grid-cell', this);
    this.dataGrid.offEvent('blur.ids-data-grid-cell', this);
  }

  disconnectedCallback() {
    // NOTE: bypassing super.disconnectedCallback() for performance reasons
    this.offEvent('focusin.ids-cell', this);
    this.offEvent('focusout.ids-cell', this);

    // Clean up data grid event listeners to prevent memory leaks
    if (this.rootNode) {
      const dataGrid = this.rootNode as any;
      dataGrid.offEvent?.('blur.ids-data-grid-cell', this);
      dataGrid.offEvent?.('focus.ids-data-grid-cell', this);
      dataGrid.offEvent?.('focusout.endcelledit', this.dataGrid);
    }
  }

  get isDraggable(): boolean {
    return typeof this.formatter === 'function' && this.formatter?.name === 'rowReorder';
  }

  get isEditable(): boolean {
    if (this.classList.contains('is-readonly') || this.classList.contains('is-disabled')) return false;

    const column = this.column;
    if (typeof column.formatter === 'function' && column.formatter?.name === 'selectionCheckbox') return true;
    if (typeof column.formatter === 'string' && column.formatter === 'selectionCheckbox') return true;

    if (!column.editor) return false;
    const columnEditor = this.dataGrid.editors.find((editor) => editor.type === column?.editor?.type);
    return !!columnEditor?.editor;
  }

  /**
   * Reference to the data grid parent
   * @returns {IdsDataGrid} the data grid parent
   */
  get dataGrid() {
    if (!this.rootNode) this.rootNode = (this.getRootNode() as any).host;
    return (this.rootNode) as IdsDataGrid;
  }

  /**
   * Gets data grid row below this cell
   * @returns {IdsDataGridRow} row below
   */
  get rowBelow(): IdsDataGridRow | null {
    if (this.dataGrid.treeGrid) {
      let nextRow = this.parentElement?.nextElementSibling as IdsDataGridRow;

      // traverse nextElementSiblings until visible row is found
      while (nextRow?.hasAttribute('hidden')) {
        nextRow = nextRow?.nextElementSibling as IdsDataGridRow;
      }

      return nextRow ?? null;
    }

    return this.dataGrid.rowByIndex(this.rowIndex + 1) ?? null;
  }

  /**
   * Gets data grid row above this cell
   * @returns {IdsDataGridRow} row above
   */
  get rowAbove(): IdsDataGridRow | null {
    if (this.dataGrid.treeGrid) {
      let prevRow = this.parentElement?.previousElementSibling as IdsDataGridRow;

      // traverse previousElementSiblings until visible row is found
      while (prevRow?.hasAttribute('hidden')) {
        prevRow = prevRow?.previousElementSibling as IdsDataGridRow;
      }

      return prevRow ?? null;
    }

    return this.dataGrid.rowByIndex(this.rowIndex - 1) ?? null;
  }

  /**
   * Get the cell above this cell
   * @returns {IdsDataGridCell | null} the cell above this cell
   */
  get cellAbove(): IdsDataGridCell | null {
    return this.dataGrid?.findNextNavigableCell(this, IdsDirection.Up) ?? null;
  }

  /**
   * Get the cell below this cell
   * @returns {IdsDataGridCell | null} the cell below this cell
   */
  get cellBelow(): IdsDataGridCell | null {
    return this.dataGrid?.findNextNavigableCell(this, IdsDirection.Down) ?? null;
  }

  /**
   * Get the cell to the left of this cell
   * @returns {IdsDataGridCell | null} the cell to the left of this cell
   */
  get cellLeft(): IdsDataGridCell | null {
    return this.dataGrid?.findNextNavigableCell(this, IdsDirection.Left) ?? null;
  }

  /**
   * Get the cell to the right of this cell
   * @returns {IdsDataGridCell | null} the cell to the right of this cell
   */
  get cellRight(): IdsDataGridCell | null {
    return this.dataGrid?.findNextNavigableCell(this, IdsDirection.Right) ?? null;
  }

  /**
   * Get the next editable cell to the left of this cell
   * @returns {IdsDataGridCell | null} the next editabled cell to the left of this cell
   */
  get cellLeftEditable(): IdsDataGridCell | null {
    if (!this.dataGrid?.isEditable) return null;
    if (this?.columnIndex === 0 && this?.rowIndex === 0) return null;

    let nextCell = this.cellLeft;
    const maxColumns = this.dataGrid?.visibleColumns?.length || 0;
    let attempts = 0;

    while (nextCell && !nextCell.isEditable && attempts < maxColumns) {
      nextCell = nextCell.cellLeft;
      attempts++;
    }

    // If we've tried all columns and found no editable cell, return null
    if (attempts >= maxColumns || !nextCell?.isEditable) {
      return null;
    }

    return nextCell;
  }

  /**
   * Get the next editable cell to the right of this cell
   * @returns {IdsDataGridCell | null} the next editabled cell to the right of this cell
   */
  get cellRightEditable(): IdsDataGridCell | null {
    if (!this.dataGrid?.isEditable) return null;

    let nextCell = this.cellRight;
    const maxColumns = this.dataGrid?.visibleColumns?.length || 0;
    let attempts = 0;

    while (nextCell && !nextCell.isEditable && attempts < maxColumns) {
      nextCell = nextCell.cellRight;
      attempts++;
    }

    // If we've tried all columns and found no editable cell, return null
    if (attempts >= maxColumns || !nextCell?.isEditable) {
      return null;
    }

    return nextCell;
  }

  /**
   * Update this cell's dataset and refresh
   * @param {string} value the new data for the cell
   * @param {boolean} refresh if true, rerender the cell
   */
  updateData(value: string, refresh = true) {
    const rowIndex = this.rowIndex;
    const record = this.dataGrid?.data[rowIndex];
    const columnIndex = this.columnIndex;

    // a cell exists in a row of a visible-columns
    const visibleColumns = this.dataGrid?.visibleColumns;

    const column = visibleColumns[columnIndex];
    if (!column?.field) return;

    const updatedRecord = {
      ...record,
      [column.field]: value,
    };

    this.editor?.change?.(value);

    if (refresh) {
      this.dataGrid?.updateDatasetAndRefresh?.(this.rowIndex, updatedRecord, false);
    } else {
      this.dataGrid?.updateDataset?.(this.rowIndex, updatedRecord, false);
    }
  }

  // Add memoization for frequently accessed properties
  #memoizedColumn?: IdsDataGridColumn;

  // Add memoization for frequently accessed properties
  #memoizedColumnIndex?: number;

  /**
   * Get the column definition
   * @returns {IdsDataGridColumn} the current cells column
   */
  get column() {
    if (!this.#memoizedColumn || this.columnIndex !== this.#memoizedColumnIndex) {
      this.#memoizedColumn = this.dataGrid?.visibleColumns[this.columnIndex];
      this.#memoizedColumnIndex = this.columnIndex;
    }
    return this.#memoizedColumn;
  }

  /**
   * Get the column header cell element
   * @returns {IdsDataGridColumn} the current cells column
   */
  get columnHeader() {
    return this.dataGrid?.header?.columns[this.columnIndex];
  }

  /**
   * Gets the column # in which this cell exists
   * @returns {number} the column-index
   */
  get columnIndex(): number {
    return Number(this.getAttribute?.('aria-colindex') ?? 1) - 1;
  }

  /**
   * Gets the row-index # in which this cell exists
   * @returns {number} the row-index
   */
  get rowIndex(): number {
    return Number(this.parentElement?.getAttribute?.('row-index') ?? -1);
  }

  get formatter(): IdsDataGridColumn['formatter'] {
    return this.column?.formatter;
  }

  /**
   * Get row of table cell
   * @returns {number} table row index
   */
  get row(): number {
    return this.rowIndex;
  }

  /**
   * Get data value of this cell
   * @returns {any} the data value of this cell
   */
  get value(): any {
    // NOTE: the editor is a singleton, so we must ensure the input is still in this cell's DOM.
    if (this.editor && this.contains(this.editor?.input ?? null)) {
      return this.editor?.value?.();
    }

    const column = this.dataGrid?.visibleColumns?.[this.columnIndex];
    const record = this.dataGrid?.data?.[this.rowIndex];

    return record?.[(column?.field ?? -1)] ?? this.textContent ?? '';
  }

  /**
   * Get the default/original value of this cell
   * @returns {any} the default value of this cell
   */
  get defaultValue(): any {
    const column = this.dataGrid?.visibleColumns?.[this.columnIndex];
    const record = this.dataGrid?.data?.[this.rowIndex];
    const dirtyCells = record?.dirtyCells ?? [];
    return dirtyCells.find((row: Record<string, any>) => row?.columnId === column?.id)?.originalValue ?? this.value;
  }

  /**
   * Rerender a cell - may be used later
   */
  renderCell() {
    const dataGrid = this.dataGrid;

    const rowIndex = this.rowIndex;
    const rowData: Record<string, any> | undefined = dataGrid?.data[rowIndex];

    if (rowData?.isGroupRow || rowData?.isGroupFooterRow) {
      return;
    }

    if (dataGrid?.virtualScroll) {
      const tooManyColumns = dataGrid.columns.length > dataGrid.TOO_MANY_COLUMNS;

      const columnsStale = dataGrid.hasAttribute('columns-stale');
      const columnsFresh = !columnsStale;

      const columnHeaders = this.columnHeader;
      const columnOffScreen = columnHeaders?.hasAttribute('column-offscreen');

      if (tooManyColumns && columnsFresh && columnOffScreen) {
        // NOTE: skip renderCell if cell is NOT on-screen
        return;
      }

      const columnOnScreen = columnHeaders?.hasAttribute('column-onscreen');
      const columnOffScreenLeft = columnHeaders?.hasAttribute('column-offscreen-left');
      const columnOffScreenRight = columnHeaders?.hasAttribute('column-offscreen-right');
      this.toggleAttribute('column-onscreen', columnOnScreen);
      this.toggleAttribute('column-offscreen', columnOffScreen);
      this.toggleAttribute('column-offscreen-left', columnOffScreenLeft);
      this.toggleAttribute('column-offscreen-right', columnOffScreenRight);
    }

    const column = this.column;
    const columnIndex = this.columnIndex;

    const selectedLabel = this.#retrieveSelectedEditorLabel();
    let dataToMap = rowData;
    if (dataToMap && selectedLabel && this.column.field) {
      dataToMap = { ...dataToMap, [this.column.field]: selectedLabel };
    }

    const template = IdsDataGridCell.template(dataToMap, column, rowIndex, dataGrid);
    if (this.innerHTML !== template) {
      try {
        this.innerHTML = template;
      } catch (e) {
        requestAnimationFrame(() => {
          this.innerHTML = template;
        });
      }
    }

    const cssClasses = this?.cellCssClasses(column, columnIndex, template) ?? '';

    if (cssClasses) {
      this.classList.remove(...this.classList);
      this.classList.add(...cssClasses.split(' '));
    }

    this.toggleAttribute('hidden', column.hiddenAtRuntime === true);

    const cssPart = this?.cellCssPart(column, columnIndex) ?? '';
    if (cssPart) this.setAttribute('part', cssPart);

    if (dataGrid.lastActiveCell?.cell === columnIndex && dataGrid.lastActiveCell?.row === rowIndex) {
      this.onFocus();
    }

    const spanCount = rowData?.rowspan?.[column.id] || 1;
    if (spanCount > 1) {
      this.setRowspan(spanCount);
    }
  }

  /**
   * Return the cells' css part
   * @param {IdsDataGridColumn} column - the column definition
   * @param {number} columnIndex - the column index
   * @returns {string} The css part string for the cell
   */
  cellCssPart(column: IdsDataGridColumn, columnIndex: number): string {
    const dataGrid = this.dataGrid;
    const rowIndex = this.rowIndex;
    const rowData = dataGrid.data[rowIndex];

    const cacheKey = IdsDataGridCell.cacheKey(rowIndex, rowData, column, dataGrid);
    const cellCacheKey = `${cacheKey}:${dataGrid.cacheCellPart}`;

    if (IdsDataGridCell.cellCache.has(cellCacheKey)) {
      return IdsDataGridCell.cellCache.get(cellCacheKey)!;
    }

    let part = column.cssPart || 'cell';
    const rowSelection = dataGrid?.rowSelection;
    const isSelectable = rowSelection === 'mixed' || rowSelection === 'single' || rowSelection === 'multiple';
    if (isSelectable && rowData.rowSelected) {
      if (column.cellSelectedCssPart) part = column.cellSelectedCssPart;
      else part = 'cell-selected';
    }

    if (typeof part === 'function') {
      part = part({
        rowIndex,
        columnIndex,
        recordIndex: this.parentRow?.recordIndex ?? null,
        rowData
      });
    }

    IdsDataGridCell.cellCache.set(cellCacheKey, part);

    return part;
  }

  /**
   * Return the cells' css classes
   * @param {IdsDataGridColumn} column - the column definition
   * @param {number} columnIndex - the column index
   * @param {string} content - the cells html or text content
   * @returns {string} The classList string for the cell
   */
  cellCssClasses(column: IdsDataGridColumn, columnIndex: number, content: string): string {
    const dataGrid = this.dataGrid;
    const rowIndex = this.rowIndex;
    const rowData = dataGrid.data[rowIndex];

    const cacheKey = IdsDataGridCell.cacheKey(rowIndex, rowData, column, dataGrid);
    const cellCacheKey = `${cacheKey}:${dataGrid.cacheCellClass}`;

    if (IdsDataGridCell.cellCache.has(cellCacheKey)) {
      return IdsDataGridCell.cellCache.get(cellCacheKey)!;
    }

    const isDirtyCell = (currentRow: Record<string, unknown>): boolean => {
      if (!currentRow.dirtyCells) return false;
      return (currentRow.dirtyCells as any).findIndex((item: any) => item.cell === columnIndex) !== -1;
    };

    const isInvalidCell = (currentRow: Record<string, unknown>): boolean => {
      if (!currentRow.invalidCells) return false;
      return (currentRow.invalidCells as any).findIndex((item: any) => item.cell === columnIndex) !== -1;
    };

    const isReadonly = (): boolean => {
      if (column.readonly && column?.readonly === true) return true;
      if (typeof column?.readonly === 'function') return column?.readonly(rowIndex, content, column, rowData);
      return false;
    };

    const isColumnDisabled = (): boolean => {
      if (!column?.disabled) return false;
      if (typeof column?.disabled === 'function') return column?.disabled(rowIndex, content, column, rowData);
      if (typeof column?.disabled === 'boolean') return column?.disabled;
      return false;
    };

    const isUppercase = (): boolean => {
      if (typeof column?.uppercase === 'function') return column.uppercase('body-cell', column, rowIndex, content, rowData);
      return (column?.uppercase === 'true' || column?.uppercase === true);
    };

    const frozenLast = dataGrid?.leftFrozenColumns.length;

    const hasReadonlyClass = isReadonly();
    const hasDisabledClass = this.parentRow?.disabled || isColumnDisabled();
    const hasUppercaseClass = isUppercase();
    const hasInlineClass = column?.editor && column?.editor?.inline;
    const formatterName = typeof column?.formatter === 'function' ? column.formatter?.name : column?.formatter;
    const hoverable = ['textmask'].includes(formatterName || '');
    const editorType = column.editor?.type;
    const displayFormatterName = formatterName === 'rowReorder' ? 'row-reorder' : formatterName;

    let cssClasses = 'ids-data-grid-cell';
    cssClasses += editorType ? ` is-${editorType}` : '';
    cssClasses += column.excludeReadonlyStyling ? ' exclude-readonly-style' : '';
    cssClasses += hasReadonlyClass ? ' is-readonly' : '';
    cssClasses += hasDisabledClass ? ' is-disabled' : '';
    cssClasses += hasUppercaseClass ? ' is-uppercase' : '';
    cssClasses += isDirtyCell(rowData) ? ' is-dirty' : '';
    cssClasses += isInvalidCell(rowData) ? ' is-invalid' : '';

    if (column.editor?.type === 'input') {
      // Field indicator — triangle always shows, class varies by type
      const fieldValue = rowData[column.field || ''];
      const hasFieldIndicatorSetup = !!column?.fieldIndicator;
      const fieldIndicatorType = typeof column?.fieldIndicator === 'function'
        ? column.fieldIndicator(rowIndex, fieldValue, column, rowData)
        : column?.fieldIndicator;
      cssClasses += hasFieldIndicatorSetup ? ' field-indicator-cell' : '';
      cssClasses += fieldIndicatorType
        ? ` has-field-indicator field-indicator-${fieldIndicatorType}`
        : '';
    }
    cssClasses += column?.align ? ` align-${column?.align}` : '';
    cssClasses += column?.whiteSpace && ['normal', 'nowrap', 'pre', 'pre-line', 'pre-wrap', 'break-spaces'].includes(column.whiteSpace) ? ` white-space-${column.whiteSpace}` : '';
    cssClasses += formatterName ? ` formatter-${displayFormatterName}` : '';
    cssClasses += formatterName === 'hyperlink' && column?.editor?.type === 'dropdown' ? ' has-editor-dropdown' : '';
    cssClasses += column?.frozen ? ` frozen frozen-${column?.frozen}${columnIndex + 1 === frozenLast ? ' frozen-last' : ''}` : '';
    cssClasses += column?.editor ? ` is-editable` : '';
    cssClasses += hoverable ? ` is-hoverable` : '';
    cssClasses += hasInlineClass && (!hasReadonlyClass || (hasReadonlyClass && !column.excludeReadonlyStyling)) ? ` is-inline` : '';
    cssClasses += formatterName === 'selectionCheckbox' ? ' is-editable' : '';

    IdsDataGridCell.cellCache.set(cellCacheKey, cssClasses);

    return cssClasses;
  }

  /**
   * Set the active cell for focus
   * @param {boolean} nofocus If true, do not focus the cell
   * @param {string} reason Reason the method was called (e.g. 'keyboard', 'mouse', 'redraw', 'internal', 'click')
   * @returns {object} the current active cell
   */
  activate(nofocus: boolean = false, reason?: IdsDataGridActivationReason): IdsDataGridCell {
    const previousActiveCell = this.dataGrid.activeCell.node;
    if (previousActiveCell === this && this.dataGrid.virtualScroll && this.isEditing) return this;
    this.dataGrid.activeCell.node = this;

    // Center column on mobile when cell is clicked with autoSize dropdown
    if (getSpecs().isMobile && reason === 'click' && this.column?.editor?.type === 'dropdown' && this.column?.editor?.editorSettings?.autoSize) {
      this.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    }

    if (!nofocus) {
      this.dataGrid.shadowRoot?.querySelector('ids-data-grid-cell[tabindex="0"]')?.setAttribute('tabindex', '-1');
      this.tabIndex = 0;
      this.focus();
    }

    if (previousActiveCell === undefined || (previousActiveCell !== this && document.hasFocus())) {
      this.dataGrid.triggerEvent('activecellchanged', this.dataGrid, {
        detail: {
          elem: this, activeCell: this.dataGrid.activeCell, previousActiveCell, reason
        }
      });
    }
    return this;
  }

  /** Previous Value before Editing */
  originalValue: unknown;

  /** Previous Selected Index before Editing */
  originalSelectedIndex: number = -1;

  /** Previous Invalid state before reseting */
  previousInvalidState = '';

  /** If currently in edit mode */
  isEditing?: boolean;

  /**
   * Validate cell
   */
  validateCell() {
    const column = this.column;
    if (!column.editor) return;

    const columnEditor = this.dataGrid.editors.find((obj) => obj.type === column?.editor?.type);
    if (!columnEditor || !columnEditor.editor) return;

    this.originalValue = this.value;
    this.originalSelectedIndex = this.editor?.input?.selectedIndex ?? -1;

    this.editor = columnEditor.editor;
    this.editor.init(this);

    const input = this.editor?.input as IdsDataGridEditors | undefined;

    if (!input) return;

    const editorType = (this.editor?.type as string);
    input?.offEvent('focusout', input);
    input?.offEvent('close', input);

    if (['input', 'tree'].includes(editorType) && input?.setDirtyTracker) {
      input?.setDirtyTracker(input?.value as any);
    }

    if (['input', 'tree'].includes(editorType) && input?.checkValidation) {
      (<IdsInput>input)?.checkValidation();
    }

    if (editorType === 'dropdown') {
      (<IdsDropdown>input)?.input?.checkValidation();
    }

    if (editorType === 'timepicker' || editorType === 'datepicker') {
      (<IdsTriggerField>input)?.checkValidation();
    }

    const isValid = input?.isValid;
    const newValue = this.editor?.save(this);
    this.#saveCellValue(newValue?.value);

    if (!isValid) this.#saveValidState(input?.validationMessages);
    if (this.isInValid && isValid) this.#resetValidState();
    this.isInValid = !isValid;

    this.editor?.destroy(this);
    this.renderCell();
  }

  /**
   * Start Edit Mode
   * @param {MouseEvent} clickEvent event passed if activated by click (vs keyboard)
   * @param {boolean} trigger whether to trigger the beforecelledit event
   */
  startCellEdit(clickEvent?: MouseEvent, trigger: boolean = true) {
    // end previous cell edit
    if (this.dataGrid.activeCellEditor instanceof IdsDataGridCell && this.dataGrid.activeCellEditor !== this) {
      this.dataGrid.activeCellEditor.endCellEdit();
    }

    const column = this.column;
    if (!column.editor) return;
    const columnEditor = this.dataGrid.editors.find((obj) => obj.type === column?.editor?.type);
    if (!columnEditor || !columnEditor.editor || this.isEditing) return;

    // Init Editor
    let canEdit: any = !(this.classList.contains('is-readonly') || this.classList.contains('is-disabled'));
    if (!canEdit) {
      return;
    }

    if (trigger) {
      this.dataGrid.triggerEvent('beforecelledit', this.dataGrid, {
        detail: {
          elem: this,
          editor: this.editor,
          column,
          data: this.dataGrid.data[this.dataGrid.activeCell.row],
          selectedIndex: this.editor?.input?.selectedIndex || -1,
          response: (veto: boolean | Promise<void | boolean>) => {
            canEdit = veto;
          }
        }
      });
    }

    if (trigger && (canEdit instanceof Promise || canEdit?.constructor?.name === 'AsyncFunction')) {
      (canEdit instanceof Promise ? canEdit : canEdit())
        .then((veto?: boolean) => { if (veto !== false) this.#startCellEdit(column, columnEditor, clickEvent, trigger); })
        .catch(() => { /** ignore if promise rejected */ });
    } else if (!trigger || canEdit !== false) {
      this.#startCellEdit(column, columnEditor, clickEvent, trigger);
    }
  }

  /* Start Cell Edit */
  #startCellEdit(
    column: IdsDataGridColumn,
    columnEditor: { type: string, editor?: IdsDataGridEditor },
    clickEvent?: MouseEvent,
    trigger: boolean = true
  ) {
    if (!columnEditor || !columnEditor.editor || this.isEditing) return;

    this.detachTooltipEvents();
    this.originalValue = this.value;
    this.originalSelectedIndex = Number(this.querySelector('[data-index]')?.getAttribute('data-index') ?? -1);
    this.editor = columnEditor.editor;
    this.editor.clickEvent = clickEvent;

    const editorType = this.editor.type;

    // The cell value is the source of truth for every editor. Dropdowns refresh
    // their display value and selected index from the editor after initialization.
    this.classList.add('is-editing');
    this.editor.init(this);

    // Refresh dropdown's original values on each edit entry (not just when -1)
    // This ensures stale values from prior edits don't persist after data changes (e.g., sort, filter)
    if (editorType === 'dropdown') {
      const input = (this.editor?.input as IdsDropdown);
      const editorIndex = input?.selectedIndex;
      if (typeof editorIndex === 'number' && editorIndex >= 0) {
        this.originalValue = (input?.selectedOption as IdsListBoxOption)?.value;
        this.originalSelectedIndex = editorIndex;
      }
    }

    const expandButton = this.querySelector('.expand-button');
    if (expandButton) {
      expandButton.removeAttribute('disabled');
      this.offEvent('mousedown', expandButton);
      this.onEvent('mousedown', expandButton, (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        this.endCellEdit();
        const row = this.parentElement as IdsDataGridRow;
        row?.toggleExpandCollapse();
      });
    }

    // Set states
    if (this.classList.contains('is-invalid')) {
      this.classList.remove('is-invalid');
      this.isInValid = true;
    }
    if (column.editor?.inline) this.classList.add('is-inline');
    this.isEditing = true;

    // Pass column text alignment rules into the cell editor
    if (column.align) {
      let columnAlign = column.align;
      if (columnAlign === 'left') columnAlign = 'start';
      if (columnAlign === 'right') columnAlign = 'end';
      this.editor?.input?.setAttribute('text-align', `${columnAlign}`);
    }

    // Save on Click Out Event
    if (['datepicker', 'timepicker', 'lookup'].includes(editorType)) {
      // When the popup is closed, end the edit
      this.editor.input?.onEvent('focusout', this.editor.input, () => {
        requestAnimationFrame(() => {
          if (this.editor?.popup?.visible || this.contains(this.dataGrid!.shadowRoot!.activeElement)) return;
          this.tabIndex = -1;
        });
      });

      // When the popup is closed, end the edit
      this.onEvent('hide.popup', this.editor?.popup, () => {
        this.offEvent('hide.popup');
        this.endCellEdit();
        this.focus();
      });
    } else if (editorType === 'multiselect') {
      this.editor.input?.onEvent('close', this.editor.input, () => {
        this.endCellEdit();
        this.focus();
      });

      this.editor.input?.onEvent('keydown', this.editor.input, (e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          e.preventDefault();
        }
      });
    } else if (editorType === 'dropdown') {
      this.editor.input?.onEvent('close', this.editor.input, () => {
        this.endCellEdit();
        this.focus();
      });
    } else if (editorType === 'password') {
      const maskIcon = this.querySelector('ids-icon');

      // stop events when clicking the maskIcon to prevent deselecting the input when editing
      this.offEvent('mousedown', maskIcon);
      this.onEvent('mousedown', maskIcon, (e) => {
        e.stopPropagation();
        e.preventDefault();
      });

      this.editor.input?.onEvent('focusout', this.editor.input, () => {
        this.dataGrid.clearActiveCellCache(true);
        this.endCellEdit();
      });
    } else {
      this.editor.input?.onEvent('focusout', this.editor.input, () => {
        this.dataGrid.clearActiveCellCache(true);
        this.endCellEdit();
      });
    }

    this.onEvent('keyup', this.editor?.input, () => {
      this.dataGrid.updateActiveCellCache(this.row, this.columnIndex, this.editor?.input?.value || '');
    });

    this.onEvent('keydown', this.editor?.input, (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        this.dataGrid.clearActiveCellCache();
      }
    });

    this.onEvent('focusout.endcelledit', this.dataGrid, () => {
      if ((this.editor?.popup as IdsDatePickerPopup)?.monthYearPicklist?.visible) return;
      if (!this.dataGrid.contains(document.activeElement)) {
        this.endCellEdit();
      }
    });

    const editorTypeSource = ['dropdown'];
    const hasAsyncSource = editorTypeSource.includes(editorType)
      && ((column.editor?.editorSettings?.source instanceof Function)
        || (column.editor?.editorSettings?.beforeShow instanceof Function));

    if (trigger && !hasAsyncSource) {
      requestAnimationFrame(() => {
        this.dataGrid?.triggerEvent('celledit', this.dataGrid, {
          detail: {
            elem: this,
            editor:
              this.editor,
            column,
            data: this.dataGrid.data[this.row],
            selectedIndex: this.originalSelectedIndex,
          }
        });
      });
    }

    // Add a small delay before starting new edit to ensure cleanup
    this.dataGrid.activeCellEditor = this;
  }

  /**
   * Re-captures originalSelectedIndex and fires celledit after async source options load.
   * @param {IdsDataGridColumn} column the column config
   * @param {string} editorType the editor type
   * @param {boolean} trigger whether to fire the celledit event
   */
  syncEditorStateAfterLoad(
    column: IdsDataGridColumn,
    editorType: string,
    trigger: boolean
  ): void {
    if (editorType === 'dropdown') {
      const input = (this.editor?.input as IdsDropdown);
      let editorIndex = input?.selectedIndex ?? -1;
      const selectedOption = (input?.selectedOption as IdsListBoxOption);

      // If selectedIndex is still -1, try to find the matching option by value and label
      if (editorIndex === -1 && input) {
        const currentValue = input.value;
        const label = selectedOption?.label ?? '';

        if (currentValue) {
          const options = input.options ?? [];
          const matchIdx = options.findIndex(
            (opt: IdsListBoxOption) => opt.value === currentValue && opt.label === label
          );
          if (matchIdx >= 0) {
            editorIndex = matchIdx;
          }
        }
      }

      if (typeof editorIndex === 'number' && editorIndex >= 0) {
        this.originalValue = selectedOption?.value ?? input?.value;
        this.originalSelectedIndex = editorIndex;
      }
    }

    if (trigger) {
      this.dataGrid?.triggerEvent('celledit', this.dataGrid, {
        detail: {
          elem: this,
          editor:
            this.editor,
          column,
          data: this.dataGrid.data[this.row],
          selectedIndex: this.originalSelectedIndex,
        }
      });
    }
  }

  /**
   * Retrieve the selected label from a editor when the column uses the text formatter.
   * @private
   * @returns {string | null} The selected option label, or null if not applicable
   */
  #retrieveSelectedEditorLabel(): string | null {
    const formatterName = typeof this.column?.formatter === 'function'
      ? this.column?.formatter.name : this.column?.formatter;

    if (formatterName !== 'text') return null;

    if (this.column?.editor?.type === 'dropdown'
      && this.editor?.input?.selected?.label) {
      return this.editor?.input?.selected?.label;
    }

    return null;
  }

  /**
   * End cell edit
   * @param {boolean} clearCache True to clear cache. Default is false.
   */
  endCellEdit(clearCache: boolean = false) {
    const column = this.column;
    const input = this.editor?.input as IdsDataGridEditors | undefined;

    if (!input) return;

    if (clearCache) this.clearCache();

    this.offEvent('focusout.endcelledit', this.dataGrid);
    this.offEvent('keydown', this.editor?.input);
    this.offEvent('keyup', this.editor?.input);

    const editorType = (this.editor?.type as string);
    input?.offEvent('focusout', input);
    input?.offEvent('close', input);

    if (['input', 'tree'].includes(editorType) && input?.setDirtyTracker) {
      input?.setDirtyTracker(input?.value as any);
    }

    if (['input', 'tree'].includes(editorType) && input?.checkValidation) {
      (<IdsInput>input)?.checkValidation();
    }

    if (editorType === 'dropdown') {
      const dropdownInput = <IdsDropdown>input;
      dropdownInput?.input?.checkValidation();

      if (dropdownInput.autoSize && dropdownInput.inputAutoSize) {
        dropdownInput.triggerEvent('restore-column-width', dropdownInput, {
          detail: { columnId: this.column.id }
        });
      }
    }

    if (editorType === 'lookup') {
      (<IdsLookup>input)?.input?.checkValidation();
    }

    if (editorType === 'timepicker' || editorType === 'datepicker') {
      (<IdsTriggerField>input)?.checkValidation();
    }

    const isDirty = column.editor?.editorSettings?.dirtyTracker && (input?.isDirty || input?.input.isDirty);
    const isDirtyCheckbox = column.editor?.editorSettings?.dirtyTracker && editorType === 'checkbox' && input?.isDirty;

    const isValid = input?.isValid;
    const newValue = this.editor?.save(this);

    // Normalize values by stripping tabs and newlines for comparison
    const normalize = (val: any): any => {
      if (typeof val === 'string') {
        return val.replace(/[\t\n\r]/g, '');
      }
      return val;
    };

    const normalizedNewValue = normalize(newValue?.value);
    const normalizedOriginalValue = normalize(this.originalValue);

    // Only save if the value actually changed from the original (after normalization)
    // This preserves whitespace characters like \n in the original value
    if (normalizedNewValue !== normalizedOriginalValue) {
      this.#saveCellValue(newValue?.value);
    }

    // Save dirty state: for dropdowns check dirtyTracker setting, for others use isDirty flag
    let shouldCheckDirty = isDirty || isDirtyCheckbox;
    if (editorType === 'dropdown') {
      shouldCheckDirty = !!column.editor?.editorSettings?.dirtyTracker;
    }

    if (shouldCheckDirty) {
      const dirtyCheckValue = newValue?.dirtyCheckValue ?? newValue?.value;
      if (normalize(dirtyCheckValue) !== normalizedOriginalValue) {
        this.#saveDirtyState(dirtyCheckValue);
      } else if (this.classList.contains('is-dirty')) {
        this.#saveDirtyState(normalize(dirtyCheckValue));
      }
    } else if (this.classList.contains('is-dirty') && !!column.editor?.editorSettings?.dirtyTracker) {
      // Clean up programmatic dirty state (e.g. via setDirtyCells API)
      // when editor input wasn't modified but cell was already marked dirty
      this.#saveDirtyState(normalize(newValue?.dirtyCheckValue ?? newValue?.value));
    }
    if (!isValid) this.#saveValidState(input?.validationMessages);
    if (this.isInValid && isValid) this.#resetValidState();
    this.isInValid = !isValid;

    // Capture selected index from the editor before destroying it
    const editorSelectedIndex = (this.editor?.input as any)?.selectedIndex ?? -1;

    this.editor?.destroy(this);
    this.renderCell();
    this.isEditing = false;
    this.classList.remove('is-editing');

    // Reset focus and tabindex
    this.tabIndex = -1;
    this.blur();

    // Use editor's selected index if available, fall back to DOM attribute
    const domSelectedIndex = Number(this.querySelector('[data-index]')?.getAttribute('data-index') ?? -1);
    const newSelectedIndex = domSelectedIndex >= 0 ? domSelectedIndex : editorSelectedIndex;

    this.dataGrid?.triggerEvent('endcelledit', this.dataGrid, {
      detail: {
        elem: this,
        editor: this.editor,
        column,
        data: this.dataGrid.data[this.row],
        oldValue: this.originalValue,
        newValue: newValue?.value,
        oldSelectedIndex: this.originalSelectedIndex,
        newSelectedIndex
      }
    });
    this.detachTooltipEvents();
    this.dataGrid.activeCellEditor = undefined;
    this.dataGrid.openMenu = null;
    this?.editor?.onEndCellEdit?.(this);
  }

  /* Cancel Cell Edit */
  cancelCellEdit() {
    const column = this.column;
    const input = this.editor?.input as IdsDataGridEditors | undefined;
    input?.offEvent('focusout', input);
    input?.setDirtyTracker(input?.value as any);

    this.dataGrid?.updateDataset(this.row, { [String(column?.field)]: this.originalValue });

    // Capture selected index from the editor before destroying it
    const editorSelectedIndex = (this.editor?.input as any)?.selectedIndex ?? -1;

    this.editor?.destroy(this);
    this.renderCell();
    this.isEditing = false;
    this.classList.remove('is-editing');

    // Use editor's selected index if available, fall back to DOM attribute
    const domSelectedIndex = Number(this.querySelector('[data-index]')?.getAttribute('data-index') ?? -1);
    const newSelectedIndex = domSelectedIndex >= 0 ? domSelectedIndex : editorSelectedIndex;

    this.dataGrid?.triggerEvent('cancelcelledit', this.dataGrid, {
      detail: {
        elem: this,
        editor: this.editor,
        column,
        data: this.dataGrid.data[this.row],
        oldValue: this.originalValue,
        oldSelectedIndex: this.originalSelectedIndex,
        selectedIndex: newSelectedIndex
      }
    });
    this.dataGrid.activeCellEditor = undefined;
    this.dataGrid.openMenu = null;
  }

  /**
   * Detach the editor without committing or canceling. Used when the grid state changes
   * (e.g., sort, reload) and the editor needs to be torn down immediately without performing editing.
   */
  detachEditor(): void {
    if (!this.isEditing) return;

    const input = this.editor?.input as IdsDataGridEditors | undefined;

    // Detach event handlers to prevent duplicate listeners and memory leaks
    this.offEvent('focusout.endcelledit', this.dataGrid);
    input?.offEvent('focusout', input);

    // Destroy the editor element
    this.editor?.destroy(this);
    this.renderCell();
    this.isEditing = false;
    this.classList.remove('is-editing');

    // Clean up grid references
    this.dataGrid.activeCellEditor = undefined;
    this.dataGrid.openMenu = null;
  }

  /**
   * Save cell Edit Back into data set
   * @param {any} newValue the value to coerce and save
   */
  #saveCellValue(newValue: any) {
    const column = this.column;
    this.dataGrid.resetCache(this.dataGrid?.activeCell.row);
    if (column.editor?.editorSettings?.mask === 'date') {
      newValue = this.dataGrid.localeAPI.parseDate(newValue, column.formatOptions);
    }
    this.dataGrid?.updateDataset(this.row, {
      [String(column?.field)]: newValue,
    });
  }

  /**
   * Save the dirty state info on the row
   * @param {boolean} newValue the current value
   * @param {boolean} programmaticUpdate if true, the value was changed by a progammaticUpdate update
   */
  #saveDirtyState(newValue: any, programmaticUpdate = false) {
    let rowDirtyCells = this.dataGrid.data[this.row].dirtyCells;
    if (rowDirtyCells === undefined) rowDirtyCells = [];
    const cell = Number(this.getAttribute('aria-colindex')) - 1;
    const previousCellInfo = rowDirtyCells.filter((item: any) => item.cell === cell);

    if (previousCellInfo[0] && newValue === previousCellInfo[0].originalValue) {
      const oldIndex = rowDirtyCells.findIndex((item: any) => item.cell === cell);
      rowDirtyCells.splice(oldIndex, 1);
      // Value was reset
      this?.classList.remove('is-dirty');
      this.dataGrid?.updateDataset(this.row, {
        dirtyCells: rowDirtyCells
      });
      return;
    }

    this?.classList.add('is-dirty');

    if (previousCellInfo.length === 0) {
      const originalValue = this?.editor?.type === 'checkbox' || programmaticUpdate ? this.originalValue : (this?.editor?.input as any)?.dirty?.original;

      rowDirtyCells.push({
        cell: Number(this?.getAttribute('aria-colindex')) - 1,
        columnId: this.column.id,
        originalValue,
      });
      this.dataGrid?.updateDataset(this.row, {
        dirtyCells: rowDirtyCells
      });
    }
  }

  /**
   * Track value changes for dirty tracking
   * @param {any} newValue the new value
   * @param {boolean} programmaticUpdate if true, the value was changed by a programmatic update
   */
  trackValueChange(newValue: any, programmaticUpdate = false) {
    this.#saveDirtyState(newValue, programmaticUpdate);
  }

  /**
   * Save the validation state info on the row
   * @param {any} validationMessages the current value
   */
  #saveValidState(validationMessages: any) {
    let rowInvalidCells = this.dataGrid.data[this.row].invalidCells;
    if (!rowInvalidCells) rowInvalidCells = [];
    const cell = Number(this.getAttribute('aria-colindex')) - 1;
    const previousCellInfo = rowInvalidCells.filter((item: any) => item.cell === cell);

    this?.classList.add('is-invalid');

    if (previousCellInfo.length === 0) {
      this.previousInvalidState = validationMessages.id;
      rowInvalidCells.push({
        cell: Number(this?.getAttribute('aria-colindex')) - 1,
        columnId: this.column.id,
        validationMessages
      });
      this.dataGrid?.updateDataset(this.row, {
        invalidCells: rowInvalidCells
      });
    }
  }

  /**
   * Set back the valid state
   */
  #resetValidState() {
    this?.classList.remove('is-invalid');
    this.dataGrid?.updateDataset(this.row, {
      invalidCells: undefined
    });
    this.isInValid = false;
  }

  /**
   * Moves this cell to the position of the sibling cell at the specified column index
   * @param {number} columnIndex The target column index where this cell should be moved
   */
  moveCell(columnIndex: number): void {
    const parentRow = this.parentRow;
    if (!parentRow) return;

    // Get the target sibling cell at the specified column index
    const targetCell = parentRow.cellByIndex(columnIndex);

    if (!targetCell || targetCell === this) return;

    // Move this cell to the position of the target cell
    parentRow.insertBefore(this, targetCell);
  }

  /**
   * Moves this cell to be immediately before the given target cell element.
   * Mirrors the semantics of `Node.insertBefore(newNode, referenceNode)`:
   * when `targetCell` is `null`, this cell is appended to the end of the parent row.
   * When `targetCell` is a sibling cell, this cell is inserted directly before it.
   * @param {IdsDataGridCell | null} targetCell The cell to insert this cell before,
   *   or `null` to append this cell to the end of its parent row.
   */
  moveCellBefore(targetCell: IdsDataGridCell | null): void {
    const parentRow = this.parentRow;
    if (!parentRow || targetCell === this) return;

    parentRow.insertBefore(this, targetCell);
  }

  // NOTE: check memory footprint of this caching strategy
  static cellCache = new Map<string, string>();

  get cache() {
    return IdsDataGridCell.cellCache;
  }

  /**
   * Return the Template for the cell contents
   * @param {object} rowData The data item for the row
   * @param {object} column The column data for the row
   * @param {object} rowIndex The running row-index
   * @param {IdsDataGrid} dataGrid The dataGrid instance
   * @returns {string} The template to display
   */
  static template(
    rowData: Record<string, unknown>,
    column: IdsDataGridColumn,
    rowIndex: number,
    dataGrid: IdsDataGrid
  ): string {
    const cacheKey = IdsDataGridCell.cacheKey(rowIndex, rowData, column, dataGrid);

    // NOTE: This is how we could disable cache until a proper cache-busting strategy is in place
    // delete IdsDataGridCell.cellCache[cacheKey];

    // NOTE: this type of param-based caching is good for upscroll when revisiting rows that have been seen already.
    // NOTE: we also need a content-cache that caches based on the actual data that's being rendered
    // NOTE: content-cache should probably be done in the IdsDataGridFormatters class
    if (!IdsDataGridCell.cellCache.has(cacheKey)) {
      const dataGridFormatters = (dataGrid?.formatters as any);
      let template = '';

      if (typeof column?.formatter === 'string') {
        template = dataGridFormatters[column.formatter](rowData, column, rowIndex, dataGrid);
      } else if (!dataGridFormatters[column?.formatter?.name || 'text'] && column?.formatter) {
        template = column?.formatter(rowData, column, rowIndex, dataGrid);
      } else {
        let formatterFn = '';
        if (column?.formatter?.name) {
          formatterFn = column?.formatter?.name;
        } else if (column.editor?.type && ['multiselect'].includes(column.editor.type)) {
          formatterFn = 'multiselect';
        } else {
          formatterFn = 'text';
        }
        template = dataGridFormatters[formatterFn](rowData, column, rowIndex, dataGrid);
      }

      if (rowData.invalidCells) {
        const message = (rowData.invalidCells as any).find((info: any) => info.cell === dataGrid.columnIdxById(column.id));
        if (message) template += `<ids-alert icon="error" tooltip="${message?.validationMessages[0]?.message}"></ids-alert>`;
      }

      if (column.editor?.type === 'input') {
        // Field indicator info icon with tooltip — always rendered, hidden via CSS when editing
        const fieldValue = rowData[column.field || ''];
        const indicatorType = typeof column?.fieldIndicator === 'function'
          ? column.fieldIndicator(rowIndex, fieldValue, column, rowData)
          : column?.fieldIndicator;

        if (indicatorType) {
          const indicatorMsg = typeof column?.fieldIndicatorMessage === 'function'
            ? column.fieldIndicatorMessage(rowIndex, fieldValue, column, rowData)
            : column?.fieldIndicatorMessage;

          const INDICATOR_ICONS: Record<string, string> = {
            neutral: 'empty-circle',
            info: 'info',
            success: 'success',
            caution: 'caution',
            warning: 'alert',
            error: 'error',
          };
          const iconName = INDICATOR_ICONS[indicatorType] || 'info';
          template += `<ids-icon icon="${iconName}" size="small"`
            + ` class="cell-field-indicator-icon"`
            + ` data-field-indicator-message="${indicatorMsg || ''}">`
            + `</ids-icon>`;
        }
      }

      IdsDataGridCell.cellCache.set(cacheKey, template);
    }
    return IdsDataGridCell.cellCache.get(cacheKey)!;
  }

  /**
   * Return cache key of current cell
   * @param {number} rowIndex number
   * @param {object} rowData The data item for the row
   * @param {object} column The column data for the row
   * @param {IdsDataGrid} dataGrid The dataGrid instance
   * @returns {string} Cache key
   */
  static cacheKey(
    rowIndex: number,
    rowData: Record<string, unknown>,
    column: IdsDataGridColumn,
    dataGrid: IdsDataGrid
  ): string {
    if (!dataGrid || !rowData) return '';
    const cacheHash = dataGrid?.cacheHash;
    const selected = rowData?.rowSelected ? dataGrid?.cacheSelected : dataGrid?.cacheDeselected;
    const expanded = rowData?.rowExpanded ? dataGrid?.cacheExpanded : dataGrid?.cacheCollapsed;
    return `${cacheHash}:${column.id}:${rowIndex}:${selected}:${expanded}`;
  }

  /**
   * Clears cell cache
   */
  clearCache() {
    const dataGrid = this.dataGrid;
    const rowIndex = Number(this.parentElement?.getAttribute('row-index'));
    const row = dataGrid.data[rowIndex] as any;
    const cacheKey = IdsDataGridCell.cacheKey(rowIndex, row, this.column, dataGrid);
    const cellCacheKey = `${cacheKey}:${dataGrid.cacheCellClass}`;

    IdsDataGridCell.cellCache.delete(cacheKey);
    IdsDataGridCell.cellCache.delete(cellCacheKey);
  }

  /**
   * Refreshes the cell
   */
  refreshCell() {
    this.clearCache();
    this.renderCell();
  }

  /**
   * Checks on the cell editor to see if its current state allows it to be closed.
   * @returns {boolean} true if the cell editor is able to "close"
   */
  canClose(): boolean {
    if (this.isEditing) return false;
    if (this.editor?.popup?.visible) return false;
    if (this.column?.editor?.inline) return false;

    return true;
  }

  /**
   * Sets the rowspan for this cell
   * @param {number} spanCount number of rows to span
   */
  setRowspan(spanCount: number): void {
    if (spanCount > 1) {
      this.style.gridRow = `span ${spanCount}`;
      this.style.height = `${(this.dataGrid.rowPixelHeight * spanCount)}px`;
      this.setAttribute('rowspan', String(spanCount));
    }
  }

  /**
   * Removes all tooltip-related event listeners from the data grid cell
   * @private
   * @description Detaches hover, click, popup, longpress, and focus events
   * associated with the tooltip functionality
   */
  detachTooltipEvents() {
    this.dataGrid.tooltip?.offEvent('hoverend.tooltip', this);
    this.dataGrid.tooltip?.offEvent('click.tooltip', this);
    this.dataGrid.tooltip?.offEvent('click.popup', this.dataGrid.tooltip?.popup);
    this.dataGrid.tooltip?.offEvent('longpress.tooltip', this);
    this.dataGrid.tooltip?.offEvent('focusin.tooltip', this);
    this.dataGrid.tooltip?.offEvent('focus.keyboardfocus', this);
  }
}
